import React, { useState } from "react";
import {
  ShieldCheck,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Copy,
  Download,
  ListChecks,
  FlaskConical,
  GraduationCap,
  Code2,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { MoreTools } from "@/components/MoreTools";
import { Seo } from "@/components/Seo";

const faqs = [
  {
    q: "What is a fake NPI number generator?",
    a: "A fake NPI number generator creates synthetic, checksum-valid National Provider Identifier numbers for development and testing. Hewto.ai's NPI generator produces 10-digit NPIs that pass the official CMS Luhn checksum (with the '80840' health-industry prefix) but are randomly generated — so they are safe to use in test environments, demos, and QA pipelines.",
  },
  {
    q: "Can I generate a random NPI number?",
    a: "Yes — that is exactly what this tool does. Every click generates random NPI numbers that are checksum-valid (10 digits passing the CMS Luhn check with the 80840 prefix) but not issued by CMS, making them safe, realistic random NPI numbers for testing, QA, training, and demos.",
  },
  {
    q: "Are the generated NPIs real?",
    a: "No. The numbers are randomly generated and are not issued by CMS. They pass the NPI format and Luhn checksum checks — which makes them realistic for testing — but they are not tied to any real provider in the NPPES registry. Coincidental collisions with real NPIs are possible but meaningless, since no real data is attached.",
  },
  {
    q: "How are the fake NPIs generated?",
    a: "Each NPI starts with 1 or 2 (the CMS-assigned ranges for individuals and organizations), followed by 8 random digits. The 10th digit is a Luhn (ISO/IEC 7812) check digit computed over the first 9 digits prefixed with '80840' — exactly how CMS constructs real NPIs. Every number this tool generates passes the same checksum your billing software runs.",
  },
  {
    q: "Is this a valid NPI number generator?",
    a: "Yes — every number this valid NPI number generator produces is well-formed: exactly 10 digits, starting with 1 or 2, and passing the CMS Luhn checksum with the '80840' prefix. 'Valid' here means mathematically valid in format; the NPIs are synthetic, not issued by CMS, so they won't appear in the NPPES registry.",
  },
  {
    q: "Do I need to install anything to use this NPI generator online?",
    a: "No. This is a fully online NPI generator — it runs entirely in your browser with no downloads, no signup, and no API key. Open the page, choose how many test NPI numbers you need, click generate, then copy or export to CSV.",
  },
  {
    q: "What should I use fake NPIs for?",
    a: "Use them anywhere you need realistic NPI data without touching real provider identities: software development, QA and regression testing, 837 / EDI file generation tests, clearinghouse onboarding, staff training, product demos, and populating sandbox databases. Synthetic NPIs keep PHI and real provider identifiers out of non-production systems.",
  },
  {
    q: "Can I use a fake NPI on a real claim?",
    a: "Never. Submitting a claim with a fabricated NPI is fraud and will be rejected by payers and clearinghouses at the NPPES validation step. Fake NPIs are strictly for test, development, training, and demo environments — never for production claims or any HIPAA-covered transaction.",
  },
  {
    q: "Will these NPIs pass validation?",
    a: "They will pass format and checksum validation (10 digits + Luhn with the 80840 prefix). They will not pass an NPPES registry lookup, because CMS never issued them. Use our NPI Validator tool to see the difference — it shows 'checksum valid, but no NPPES record found' for synthetic numbers.",
  },
  {
    q: "Is this test NPI number generator free?",
    a: "Yes — 100% free, no signup, no API key. Generate up to 100 checksum-valid NPIs per batch and export them to CSV in one click.",
  },
  {
    q: "How do I check whether an NPI is real?",
    a: "Use Hewto.ai's free NPI Validator. It runs the CMS Luhn checksum and then looks the number up live in the CMS NPPES registry, returning the provider's name, taxonomy, address, and status — so you can confirm an NPI is real and active before it goes on a claim.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const softwareJsonLd = {
  "@context": "https://schema.org",
  "@type": ["WebApplication", "SoftwareApplication"],
  name: "Hewto.ai Fake NPI Generator",
  applicationCategory: "DeveloperApplication",
  operatingSystem: "Web",
  browserRequirements: "Requires JavaScript",
  description:
    "Free fake NPI generator — creates synthetic, checksum-valid 10-digit NPI numbers (CMS Luhn with 80840 prefix) for development, QA, training, and demos. Bulk generation up to 100 with CSV export.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

const computeCheckDigit = (nine) => {
  const payload = "80840" + nine;
  let sum = 0;
  for (let i = 0; i < payload.length; i++) {
    let d = Number(payload[payload.length - 1 - i]);
    if (i % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return String((10 - (sum % 10)) % 10);
};

const generateNpi = (firstDigit) => {
  const first = firstDigit || (Math.random() < 0.5 ? "1" : "2");
  let nine = first;
  while (nine.length < 9) nine += String(Math.floor(Math.random() * 10));
  return nine + computeCheckDigit(nine);
};

const generateBatch = (count, entity) => {
  const firstDigit = entity === "individual" ? "1" : entity === "organization" ? "2" : null;
  const set = new Set();
  while (set.size < count) set.add(generateNpi(firstDigit));
  return Array.from(set);
};

const COUNT_OPTIONS = [1, 5, 10, 25, 50, 100];
const ENTITY_OPTIONS = [
  { value: "any", label: "Any (NPI-1 & NPI-2)" },
  { value: "individual", label: "Individual (starts with 1)" },
  { value: "organization", label: "Organization (starts with 2)" },
];

const FakeNPIGenerator = () => {
  const [count, setCount] = useState(10);
  const [entity, setEntity] = useState("any");
  const [results, setResults] = useState([]);

  const runGenerate = () => {
    setResults(generateBatch(count, entity));
    toast.success(`Generated ${count} fake NPI${count === 1 ? "" : "s"}`);
  };

  const copyAll = () => {
    navigator.clipboard.writeText(results.join("\n"));
    toast.success(`${results.length} NPIs copied to clipboard`);
  };

  const copyOne = (npi) => {
    navigator.clipboard.writeText(npi);
    toast.success(`${npi} copied`);
  };

  const exportCsv = () => {
    const csv = ["npi", ...results].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fake-npis-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageTransition>
      <Seo
        title="Random NPI Number Generator Online — Free Test NPIs | Hewto.ai"
        description="Free random NPI number generator online. Generate random NPI numbers that pass the CMS Luhn checksum — fake, checksum-valid test NPIs for development, QA, training, and demos. Bulk batches up to 100, CSV export, no signup."
        keywords="random npi number, random npi number generator, random npi generator, random npi, npi generator, fake npi number generator, npi generator online, npi number generator, test npi number generator, valid npi number generator, fake npi generator, fake npi number, test npi number, sample npi number, synthetic npi, dummy npi number, npi test data, npi generator for testing, valid test npi, npi checksum generator, luhn npi generator, 837 test data, edi test npi, npi for qa testing, healthcare test data generator, fake provider npi, npi mock data, free npi generator"
        canonical="https://www.hewto.ai/tools/fake-npi-generator"
        jsonLd={[faqJsonLd, softwareJsonLd]}
      />

      <PageHero
        eyebrow="Free tool · Random NPI Number Generator"
        title="Random NPI Number Generator"
        subtitle="Hewto.ai is a free NPI generator online — a test NPI number generator that creates random NPI numbers, checksum-valid, in one click for development, QA, training, and demos. A valid NPI number generator with bulk batches up to 100 and CSV export. No signup, nothing stored."
      />

      <section className="mx-auto max-w-4xl px-5 pb-20 sm:px-8">
        <Reveal>
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-lg shadow-brand/5 sm:p-8">
            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="fake-npi-disclaimer">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <p className="text-sm leading-relaxed text-amber-800">
                <span className="font-semibold">For testing only.</span> These NPIs are randomly
                generated and not issued by CMS. Never use them on a real claim or any
                HIPAA-covered transaction.
              </p>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="fake-npi-count" className="text-sm font-semibold text-slate-700">
                  How many NPIs?
                </label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="fake-npi-count-group">
                  {COUNT_OPTIONS.map((c) => (
                    <button
                      key={c}
                      data-testid={`fake-npi-count-${c}`}
                      onClick={() => setCount(c)}
                      className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                        count === c
                          ? "bg-brand text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="fake-npi-entity" className="text-sm font-semibold text-slate-700">
                  Entity type
                </label>
                <select
                  id="fake-npi-entity"
                  data-testid="fake-npi-entity"
                  value={entity}
                  onChange={(e) => setEntity(e.target.value)}
                  className="mt-2 h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-brand focus:outline-none"
                >
                  {ENTITY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <Button
              data-testid="fake-npi-generate-btn"
              onClick={runGenerate}
              className="mt-6 h-12 w-full rounded-xl bg-brand px-6 text-base font-semibold text-white hover:bg-brand-600"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Generate fake NPIs
            </Button>
            <p className="mt-2 text-xs text-slate-500">
              Generated in your browser — nothing is sent to or stored on our servers.
            </p>

            {results.length > 0 && (
              <div className="mt-6" data-testid="fake-npi-results">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-slate-700">
                    {results.length} checksum-valid NPI{results.length === 1 ? "" : "s"}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      data-testid="fake-npi-copy-all-btn"
                      variant="outline"
                      onClick={copyAll}
                      className="rounded-full"
                    >
                      <Copy className="mr-2 h-4 w-4" /> Copy all
                    </Button>
                    <Button
                      data-testid="fake-npi-csv-btn"
                      variant="outline"
                      onClick={exportCsv}
                      className="rounded-full"
                    >
                      <Download className="mr-2 h-4 w-4" /> Export CSV
                    </Button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {results.map((npi) => (
                    <button
                      key={npi}
                      data-testid={`fake-npi-item-${npi}`}
                      onClick={() => copyOne(npi)}
                      title="Click to copy"
                      className="group flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-left transition-colors hover:border-brand hover:bg-brand-50"
                    >
                      <span className="font-mono text-sm font-semibold tracking-wider text-slate-900">
                        {npi}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" /> Luhn
                        </span>
                        <Copy className="h-3.5 w-3.5 text-slate-300 transition-colors group-hover:text-brand" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Reveal>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-slate-50 py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-12 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <div>
              <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
                Under the hood
              </span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                How this NPI number generator works
              </h2>
              <p className="mt-4 leading-relaxed text-slate-600">
                Every NPI is a 10-digit number whose final digit is a Luhn (ISO/IEC 7812) check
                digit computed over the first 9 digits, prefixed with{" "}
                <span className="font-mono">80840</span> — the CMS issuer identifier for the US
                health-industry range. This generator follows the exact same construction CMS uses,
                so every number passes the checksum your billing software runs.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-slate-700">
                {[
                  "First digit 1 (individual / NPI-1) or 2 (organization / NPI-2) — the CMS-assigned ranges",
                  "8 random digits fill positions 2–9",
                  "10th digit computed via Luhn over '80840' + first 9 digits",
                  "Uniqueness guaranteed within every batch",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-slate-200 bg-slate-950 p-6 shadow-lg">
              <pre className="whitespace-pre text-xs leading-relaxed text-slate-200">
{`# Generating one fake NPI
1. Pick first digit: 1 or 2
2. Add 8 random digits
   → e.g. 123456789
3. Prefix with '80840'
   → 80840 123456789
4. Double alternating digits (Luhn)
5. check = (10 - (sum mod 10)) mod 10
   → 3
6. Result: 1234567893
   → passes CMS checksum ✓
   → not in NPPES (synthetic)`}
              </pre>
            </div>
            <p className="mt-4 text-sm text-slate-500">
              A valid checksum only means the number is well-formed. To check whether an NPI is
              real, run it through our{" "}
              <a href="/tools/npi-validator" className="font-semibold text-brand hover:underline">
                NPI Validator
              </a>{" "}
              — it confirms checksum and live NPPES registry status in one call.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHY FAKE NPIS */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200/40 bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-700">
              <Sparkles className="h-3.5 w-3.5" /> Why synthetic NPIs
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Realistic test data, zero compliance risk
            </h2>
          </div>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              icon: Code2,
              title: "Development & QA",
              desc: "Populate dev and staging databases, test 837 / EDI generation, and run clearinghouse onboarding without touching real provider identities.",
            },
            {
              icon: FlaskConical,
              title: "Pipeline testing",
              desc: "Feed checksum-valid NPIs through extraction, validation, and adjudication pipelines so format checks behave exactly like production.",
            },
            {
              icon: GraduationCap,
              title: "Training & demos",
              desc: "Teach billing staff and demo products with realistic-looking NPIs — keeping PHI and real provider identifiers out of classrooms and screenshots.",
            },
          ].map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand">
                  <f.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <div className="mx-auto mt-10 flex max-w-3xl items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
            <p className="text-sm leading-relaxed text-slate-600">
              Pair this with Hewto.ai's{" "}
              <a href="/tools/npi-validator" className="font-semibold text-brand hover:underline">
                NPI Validator
              </a>{" "}
              and{" "}
              <a href="/tools/npi-lookup-by-name" className="font-semibold text-brand hover:underline">
                NPI Lookup by Name
              </a>{" "}
              to round out your test-data toolkit — generate synthetic NPIs here, verify real ones
              there.
            </p>
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-5 py-24 sm:px-8">
        <Reveal>
          <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
            Fake NPI generator FAQ
          </span>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Common questions about test NPI numbers
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-8" data-testid="fake-npi-faq">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200">
                <AccordionTrigger
                  data-testid={`fake-npi-faq-trigger-${i}`}
                  className="text-left text-base font-semibold text-slate-900 hover:no-underline"
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <MoreTools current="/tools/fake-npi-generator" />

      <CTABand />
    </PageTransition>
  );
};

export default FakeNPIGenerator;
